import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import LiveChatModal from './LiveChatModal';

export const FloatingChatButton: React.FC = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [cookieBannerUp, setCookieBannerUp] = useState(false);
  const { user } = useAuth();

  // Keep clear of the cookie banner so its Accept button stays tappable.
  useEffect(() => {
    const onCookieBanner = (e: Event) => setCookieBannerUp(Boolean((e as CustomEvent).detail?.visible));
    window.addEventListener('odyvoy-cookie-banner', onCookieBanner);
    return () => window.removeEventListener('odyvoy-cookie-banner', onCookieBanner);
  }, []);

  // Track new messages
  useEffect(() => {
    if (!user) return;

    // Get last viewed timestamp from localStorage
    const lastViewedKey = `chat_last_viewed_${user.id}`;
    const lastViewed = localStorage.getItem(lastViewedKey);
    const lastViewedDate = lastViewed ? new Date(lastViewed) : new Date(0);

    const loadUnreadCount = async () => {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('conversation_id', user.id)
        .eq('role', 'assistant')
        .gt('created_at', lastViewedDate.toISOString());

      if (!error && count !== null) {
        setUnreadCount(count);
      }
    };

    loadUnreadCount();

    // Subscribe to new messages
    const channel = supabase
      .channel(`chat-messages-changes:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${user.id}`,
        },
        (payload: any) => {
          if (payload.new.role === 'assistant') {
            setUnreadCount((prev) => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleOpenChat = () => {
    setIsChatOpen(true);
    setUnreadCount(0);
    
    // Update last viewed timestamp
    if (user) {
      const lastViewedKey = `chat_last_viewed_${user.id}`;
      localStorage.setItem(lastViewedKey, new Date().toISOString());
    }
  };

  return (
    <>
      {/* Floating Chat Button with Pulse Animation */}
      <div className={`fixed right-4 sm:right-6 z-50 transition-all duration-300 ${cookieBannerUp ? 'bottom-24 sm:bottom-6' : 'bottom-4 sm:bottom-6'}`}>
        <div className="relative">
          <Button
            onClick={handleOpenChat}
            size="lg"
            className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-primary hover:bg-primary/90 text-primary-foreground animate-pulse-3"
            aria-label="Open live chat support"
          >
            <MessageCircle className="h-5 w-5" />
          </Button>
          
          {/* Unread Badge */}
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-1 text-xs font-bold rounded-full animate-scale-in"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Live Chat Modal */}
      <LiveChatModal open={isChatOpen} onOpenChange={setIsChatOpen} />
    </>
  );
};
